import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';
import './AIChat.css';

const AIChat = ({ isOpen, onClose, dataset, table, project, context }) => {
  const { token } = useAuth();
  // fetch() bypasses axios interceptor, so always read directly from localStorage as fallback
  const getToken = () => token || localStorage.getItem('jwt_token');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!input.trim()) return;

    const userMessage = input;
    setInput('');
    setError(null);

    // Add user message to UI immediately
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    }]);

    setLoading(true);

    try {
      const currentToken = getToken();
      if (!currentToken) {
        throw new Error('Please log in to use the AI assistant');
      }

      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify({
          message: userMessage,
          dataset: dataset || 'Dashboards',
          table: table || 'DEMOGRAPHICS_PY',
          project: project || 'crypto-iris-482914-u4',
          context: context
        })
      });

      let data;
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // Vercel timeout or server error returns HTML — give a clear message
        await response.text();
        throw new Error(response.status === 504 ? 'Request timed out — please try again' : `Server error (${response.status})`);
      }

      if (!response.ok) {
        const detail = data.detail;
        throw new Error(Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : (detail || 'Failed to get response'));
      }

      // Add AI response to UI
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.ai_response,
        timestamp: new Date(),
        query: data.query_used,
        resultCount: data.result_count
      }]);

    } catch (err) {
      setError(err.message);
      console.error('Chat error:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    // Clear UI immediately regardless of backend response
    setMessages([]);
    setError(null);
    try {
      await fetch(`${API_BASE_URL}/api/chat/clear-history`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        credentials: 'include'
      });
    } catch (err) {
      console.error('Error clearing history:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ai-chat-modal" onClick={onClose}>
      <div className="ai-chat-container" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="ai-chat-header">
          <h2>Data Assistant</h2>
          <div className="ai-chat-controls">
            {messages.length > 0 && (
              <button 
                className="clear-btn" 
                onClick={clearHistory}
                title="Clear conversation"
              >
                Clear
              </button>
            )}
            <button 
              className="close-btn" 
              onClick={onClose}
              title="Close chat"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="ai-chat-messages">
          {messages.length === 0 ? (
            <div className="ai-chat-welcome">
              <p>👋 Ask me questions about your data</p>
              <p className="ai-chat-hint">Example: "Show me patient demographics by county"</p>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div key={idx} className={`ai-chat-message ${msg.role}`}>
                <div className="ai-chat-message-content">
                  {msg.content}
                  {msg.query && (
                    <details className="query-details">
                      <summary>View SQL Query</summary>
                      <pre>{msg.query}</pre>
                    </details>
                  )}
                </div>
              </div>
            ))
          )}
          {loading && (
            <div className="ai-chat-message assistant loading">
              <div className="ai-chat-message-content">
                <div className="spinner"></div>
                <span>Thinking...</span>
              </div>
            </div>
          )}
          {error && (
            <div className="ai-chat-error">
              ⚠️ {error}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form className="ai-chat-input-form" onSubmit={sendMessage}>
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Ask about your data..."
            disabled={loading}
            className="ai-chat-input"
          />
          <button 
            type="submit" 
            disabled={loading || !input.trim()}
            className="ai-chat-send-btn"
          >
            Send
          </button>
        </form>

        {/* Footer */}
        <div className="ai-chat-footer">
          <small>HIPAA compliant • Read-only queries • Data encrypted</small>
        </div>
      </div>
    </div>
  );
};

export default AIChat;
